import * as PopoverPrimitive from '@radix-ui/react-popover';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import * as React from 'react';
import { cn } from '@/shared/lib/utils';

/**
 * Detect if device supports touch
 * Tooltips don't work well on touch devices per ARIA guidelines,
 * so we use Popover as a fallback on mobile
 */
const isTouchDevice = () => {
  if (globalThis.window === undefined) return false;
  return (
    'ontouchstart' in globalThis.window ||
    navigator.maxTouchPoints > 0 ||
    // @ts-expect-error - msMaxTouchPoints is IE-specific
    navigator.msMaxTouchPoints > 0
  );
};

/**
 * TooltipProvider with mobile-friendly defaults
 * - Reduced delay for faster appearance on touch devices
 * - Skip delay when moving between tooltips
 */
const TooltipProvider = ({
  delayDuration = 200,
  skipDelayDuration = 100,
  ...props
}: React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Provider>) => (
  <TooltipPrimitive.Provider
    delayDuration={delayDuration}
    skipDelayDuration={skipDelayDuration}
    {...props}
  />
);

/**
 * Hybrid Tooltip/Popover Root component
 * - Mobile (touch devices): Uses controlled Popover for tap-to-open behavior
 *   Opens on click, stays open until clicked outside or trigger clicked again
 * - Desktop: Uses Tooltip for hover behavior
 * This follows ARIA best practices as tooltips are not suitable for touch devices
 */
interface TooltipRootProps extends React.ComponentPropsWithoutRef<
  typeof TooltipPrimitive.Root
> {
  readonly children: React.ReactNode;
  /**
   * Controls whether tooltip/popover closes when clicking outside on mobile devices
   * @default true
   */
  readonly dismissOnOutsideClick?: boolean;
}

const TooltipContext = React.createContext<{
  readonly dismissOnOutsideClick: boolean;
}>({
  dismissOnOutsideClick: true,
});

const Tooltip = ({
  children,
  dismissOnOutsideClick = true,
  ...props
}: TooltipRootProps) => {
  const [isTouch] = React.useState(() => isTouchDevice());
  const [open, setOpen] = React.useState(false);

  const contextValue = React.useMemo(
    () => ({ dismissOnOutsideClick }),
    [dismissOnOutsideClick]
  );

  // On mobile: use controlled Popover with explicit open/close behavior
  if (isTouch) {
    return (
      <TooltipContext.Provider value={contextValue}>
        <PopoverPrimitive.Root open={open} onOpenChange={setOpen}>
          {children}
        </PopoverPrimitive.Root>
      </TooltipContext.Provider>
    );
  }

  // On desktop: use Tooltip
  return (
    <TooltipContext.Provider value={contextValue}>
      <TooltipPrimitive.Root {...props}>{children}</TooltipPrimitive.Root>
    </TooltipContext.Provider>
  );
};

/**
 * Hybrid Trigger component
 * Works with both Popover (mobile) and Tooltip (desktop)
 */
const TooltipTrigger = React.forwardRef<
  HTMLButtonElement,
  React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Trigger>
>((props, ref) => {
  const [isTouch] = React.useState(() => isTouchDevice());

  if (isTouch) {
    return <PopoverPrimitive.Trigger ref={ref} {...props} />;
  }

  return <TooltipPrimitive.Trigger ref={ref} {...props} />;
});
TooltipTrigger.displayName = 'TooltipTrigger';

/**
 * Hybrid Content component
 * Works with both Popover (mobile) and Tooltip (desktop)
 * On mobile: respects dismissOnOutsideClick prop to control click-outside behavior
 */
const TooltipContent = React.forwardRef<
  React.ComponentRef<typeof TooltipPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Content>
>(({ className, sideOffset = 4, ...props }, ref) => {
  const [isTouch] = React.useState(() => isTouchDevice());
  const { dismissOnOutsideClick } = React.useContext(TooltipContext);

  const contentClassName = cn(
    'z-50 overflow-hidden rounded-md border border-border bg-popover px-3 py-1.5 text-sm text-popover-foreground shadow-md',
    'animate-in fade-in-0 zoom-in-95',
    'data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95',
    'data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2',
    'data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2',
    'max-w-[90vw] sm:max-w-md',
    className
  );

  if (isTouch) {
    return (
      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Content
          ref={ref}
          sideOffset={sideOffset}
          className={cn(contentClassName, 'outline-none')}
          onInteractOutside={(event) => {
            // Control whether clicking outside closes the popover based on prop
            if (!dismissOnOutsideClick) {
              event.preventDefault();
            }
          }}
          {...(props as React.ComponentPropsWithoutRef<
            typeof PopoverPrimitive.Content
          >)}
        />
      </PopoverPrimitive.Portal>
    );
  }

  return (
    <TooltipPrimitive.Portal>
      <TooltipPrimitive.Content
        ref={ref}
        sideOffset={sideOffset}
        className={contentClassName}
        {...props}
      />
    </TooltipPrimitive.Portal>
  );
});
TooltipContent.displayName = 'TooltipContent';

export { Tooltip, TooltipProvider, TooltipTrigger, TooltipContent };
